import firebase from '../../config/firebase';
import { portalVersion } from './helpers';

/**
 * Logs session-related errors to Firestore for admin review
 * @param {Object} params - Error logging parameters
 * @param {string} params.sessionId - ID of the session where error occurred
 * @param {Error|string} params.error - The error object or message
 * @param {string} params.action - Action being performed (e.g., 'create', 'update', 'delete')
 * @param {Object} params.user - Current user object
 * @param {Object} params.additionalData - Any additional context data
 */
export const logSessionError = async ({
  sessionId,
  error,
  action,
  user,
  additionalData = {},
}) => {
  try {
    const db = firebase.firestore();
    const errorLog = {
      sessionId: sessionId || 'unknown',
      action: action || 'unknown',
      errorMessage: error?.message || String(error),
      errorStack: error?.stack || null,
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || null,
      userName: user?.displayName || null,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      additionalData,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      portalVersion: portalVersion,
    };

    await db.collection('sessionErrorLogs').add(errorLog);

    // Notify all admins about the error
    const usersSnapshot = await db.collection('users').get();
    const adminNotifications = [];

    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      const roles = userData.roles || [];
      const role = userData.role || '';
      const isAdmin =
        roles.some((r) => r.toLowerCase() === 'administrator') ||
        role.toLowerCase() === 'administrator';

      if (isAdmin) {
        const notificationRef = db.collection('notifications').doc();
        adminNotifications.push(
          notificationRef.set({
            type: 'session_error',
            sessionId: sessionId || 'unknown',
            recipientId: doc.id,
            errorAction: action || 'unknown',
            errorMessage: error?.message || String(error),
            errorUserId: user?.uid || 'anonymous',
            errorUserName: user?.displayName || 'Unknown User',
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            read: false,
            dismissed: false,
          }),
        );
      }
    });

    await Promise.all(adminNotifications);
  } catch (loggingError) {
    // Don't let logging errors break the app
    console.error('Failed to log error:', loggingError);
  }
};

/**
 * Logs general app errors (crashes, render errors, etc.) to Firestore for admin review
 * @param {Object} params - Error logging parameters
 * @param {Error|string} params.error - The error object or message
 * @param {Object} params.errorInfo - React errorInfo (componentStack, etc.)
 * @param {string} params.source - Where the error originated (e.g., 'ErrorBoundary', 'unhandledRejection')
 */
export const logAppError = async ({ error, errorInfo, source }) => {
  try {
    const db = firebase.firestore();
    const auth = firebase.auth();
    const user = auth.currentUser;

    const errorLog = {
      source: source || 'unknown',
      errorMessage: error?.message || String(error),
      errorStack: error?.stack || null,
      componentStack: errorInfo?.componentStack || null,
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || null,
      userName: user?.displayName || null,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      url: typeof window !== 'undefined' ? window.location.href : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      portalVersion: portalVersion,
    };

    await db.collection('appErrorLogs').add(errorLog);

    // Intentionally NOT creating per-admin notifications here. Doing a full
    // `users` collection read plus a batch of N writes on every crash is
    // expensive and risky precisely when the app is already in a bad state,
    // and it floods the notification bell. Admins review crashes in the
    // Error Log report (/admin/errorLog), which reads appErrorLogs directly.
  } catch (loggingError) {
    console.error('Failed to log app error:', loggingError);
  }
};
